import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * Composant pour éditer les messages personnalisés dans le panier
 * Avec collapsible pour réduire la hauteur dans le drawer
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} collapseToggle - Le bouton pour ouvrir/fermer le collapsible
 * @property {HTMLDivElement} collapsibleBody - Le contenu collapsible
 * @property {HTMLSpanElement} messagePreview - La prévisualisation du message
 * @property {HTMLButtonElement} editTrigger - Le bouton pour activer l'édition
 * @property {HTMLDivElement} charCounter - Le compteur de caractères
 * @property {HTMLSpanElement} currentCount - Le nombre de caractères actuel
 * @property {HTMLDivElement} messageDisplay - L'affichage du message en lecture seule
 * @property {HTMLDivElement} messageEditor - Le conteneur de l'éditeur
 * @property {HTMLTextAreaElement} messageTextarea - Le textarea d'édition
 * @property {HTMLButtonElement} saveBtn - Le bouton de sauvegarde
 *
 * @extends {Component<Refs>}
 */
class CartMessageEditor extends Component {
  /** @type {boolean} */
  #isEditing = false;

  /** @type {string} */
  #originalMessage = '';

  /** @type {string} */
  #propertyKey = 'message-perso';

  connectedCallback() {
    super.connectedCallback();

    this.#propertyKey = this.dataset.propertyKey || 'message-perso';
    this.#originalMessage = this.refs.messageTextarea?.value || '';

    this.refs.collapseToggle?.addEventListener('click', this.#onCollapseToggle);
    this.refs.editTrigger?.addEventListener('click', this.#onEditClick);
    this.refs.messageTextarea?.addEventListener('input', this.#onTextareaInput);
    this.refs.saveBtn?.addEventListener('click', this.#onSaveClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.collapseToggle?.removeEventListener('click', this.#onCollapseToggle);
    this.refs.editTrigger?.removeEventListener('click', this.#onEditClick);
    this.refs.messageTextarea?.removeEventListener('input', this.#onTextareaInput);
    this.refs.saveBtn?.removeEventListener('click', this.#onSaveClick);
  }

  #onCollapseToggle = () => {
    if (this.hasAttribute('data-expanded')) {
      // Ne pas fermer si en cours d'édition
      if (this.#isEditing) return;
      this.removeAttribute('data-expanded');
    } else {
      this.setAttribute('data-expanded', '');
    }
  };

  #updatePreview() {
    if (!this.refs.messagePreview) return;
    const msg = this.#originalMessage;
    this.refs.messagePreview.textContent = msg.length > 50 ? msg.substring(0, 47) + '...' : msg;
  }

  #onEditClick = () => {
    if (this.#isEditing) return;

    this.#isEditing = true;

    // S'assurer que le collapsible est ouvert
    this.setAttribute('data-expanded', '');

    this.refs.messageDisplay?.classList.add('hidden');
    this.refs.messageEditor?.classList.remove('hidden');
    this.refs.editTrigger?.classList.add('hidden');
    this.refs.charCounter?.classList.remove('hidden');

    if (this.refs.messageTextarea) {
      this.refs.messageTextarea.disabled = false;
      this.refs.messageTextarea.focus();
    }

    this.refs.saveBtn?.classList.remove('hidden');
    this.#updateCharacterCount();
  };

  #onTextareaInput = () => {
    this.#updateCharacterCount();
  };

  #updateCharacterCount() {
    if (!this.refs.messageTextarea || !this.refs.currentCount) return;

    const length = this.refs.messageTextarea.value.length;
    this.refs.currentCount.textContent = length.toString();

    if (this.refs.charCounter) {
      if (length >= 380) {
        this.refs.charCounter.classList.add('warning');
        this.refs.charCounter.classList.remove('normal');
      } else if (length >= 350) {
        this.refs.charCounter.classList.add('normal');
        this.refs.charCounter.classList.remove('warning');
      } else {
        this.refs.charCounter.classList.remove('warning', 'normal');
      }
    }
  }

  #onSaveClick = async () => {
    if (!this.refs.messageTextarea || !this.refs.saveBtn) return;

    const newMessage = this.refs.messageTextarea.value.trim();

    if (newMessage === '') {
      this.refs.messageTextarea.value = this.#originalMessage;
      if (this.refs.messageDisplay) {
        this.refs.messageDisplay.textContent = this.#originalMessage;
      }
      this.#resetToDefaultState();
      return;
    }

    if (newMessage === this.#originalMessage) {
      this.#resetToDefaultState();
      return;
    }

    this.#setLoadingState(true);

    try {
      await this.#updateCartProperty(newMessage);

      this.#originalMessage = newMessage;
      if (this.refs.messageDisplay) {
        this.refs.messageDisplay.textContent = newMessage;
      }
      this.#updatePreview();
      this.#resetToDefaultState();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du message:', error);
      alert('Une erreur est survenue lors de la modification du message. Veuillez réessayer.');
      this.#setLoadingState(false);
    }
  };

  /** @param {string} newMessage */
  async #updateCartProperty(newMessage) {
    const lineKey = this.dataset.lineKey;

    if (!lineKey) {
      throw new Error('Line key manquant');
    }

    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    const item = cart.items.find((/** @type {{ key: string }} */ i) => i.key === lineKey);
    if (!item) {
      throw new Error('Item non trouvé dans le panier');
    }

    const mergedProperties = { ...item.properties, [this.#propertyKey]: newMessage };

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set();
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: lineKey,
      quantity: item.quantity,
      properties: mergedProperties,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch('/cart/change.js', fetchConfig('json', { body }));

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      throw new Error(data.errors);
    }

    if (data.sections) {
      document.dispatchEvent(
        new CartUpdateEvent({}, 'cart-message-update', {
          itemCount: data.item_count,
          source: 'cart-message-editor',
          sections: data.sections,
        })
      );
    }

    return data;
  }

  /** @param {boolean} isLoading */
  #setLoadingState(isLoading) {
    if (!this.refs.saveBtn || !this.refs.messageTextarea) return;

    if (isLoading) {
      this.refs.saveBtn.disabled = true;
      this.refs.saveBtn.classList.add('loading');
      this.refs.saveBtn.textContent = 'Modification en cours...';
      this.refs.messageTextarea.disabled = true;
    } else {
      this.refs.saveBtn.disabled = false;
      this.refs.saveBtn.classList.remove('loading');
      this.refs.saveBtn.textContent = 'Modifier mon message';
      this.refs.messageTextarea.disabled = false;
    }
  }

  #resetToDefaultState() {
    this.#isEditing = false;

    this.refs.messageEditor?.classList.add('hidden');
    this.refs.messageDisplay?.classList.remove('hidden');
    this.refs.editTrigger?.classList.remove('hidden');
    this.refs.charCounter?.classList.add('hidden');

    if (this.refs.messageTextarea) {
      this.refs.messageTextarea.disabled = true;
    }

    this.refs.saveBtn?.classList.add('hidden');
    this.#setLoadingState(false);
  }
}

if (!customElements.get('cart-message-editor')) {
  customElements.define('cart-message-editor', CartMessageEditor);
}
